import type { ExerciseTags } from '@waedo/frontend-core/exercise-taxonomy';
import type { Actor, Step, ExercisePitch } from '../drilleditor/types';
import type { ExerciseInstructions, ExerciseSource, FormationApplication } from './curatedExercisesApi';
import { authenticatedFetch, getAuthHeaders } from './authApi';
import { getEnv } from '../utils/env';

export type LibraryContent = {
  exercise: { key: string; status: 'draft'; origin: 'original' | 'adapted' | 'reproduced'; creator: string;
    variant: 'indoor' | 'outdoor'; players?: number; durationMinutes?: number; spaceMetres?: { width: number; length: number };
    content: Partial<Record<'nl' | 'en', Partial<ExerciseInstructions> & { title: string }>>;
    actors: Actor[]; steps: Step[]; sources: string[]; formationKeys: string[]; positionRefs: string[];
    tags?: ExerciseTags | null; pitch?: ExercisePitch; applications?: FormationApplication[]; primarySourceId?: string | null };
  sources: ExerciseSource[]; formationLayouts?: Record<string, unknown>; lineage?: { id: string; content: LibraryContent };
};
export type ExerciseReuse = { training: boolean; adapt: boolean; offline: boolean; redistribute: boolean };
export type LibraryRelease = { id: string; exercise_id: string; source_revision: number; content_sha256: string;
  published_at: string; origin_kind: 'official' | 'community'; audience: 'private' | 'club' | 'public';
  content: LibraryContent; reuse: ExerciseReuse; site_selected?: boolean; status?: 'active' | 'withdrawn' | 'suspended' };
export type LibraryReleaseCard = Omit<LibraryRelease, 'content'> & {
  summary: true;
  content: { exercise: Pick<LibraryContent['exercise'], 'variant' | 'players' | 'durationMinutes' | 'tags' | 'actors' | 'pitch' | 'content'>
    & { applications?: Pick<FormationApplication, 'formationKey' | 'positionRef'>[] } };
};
export type LibraryDraft = { id: string; owner_type: 'platform' | 'user' | 'club'; owner_id: string;
  source_revision: number; state_revision: number; content: LibraryContent; can_edit: boolean; can_release: boolean; can_delete?: boolean;
  releases: LibraryRelease[]; lineage: { id: string; content: LibraryContent } | null };
export type LibraryCapabilities = { create: boolean; grants: { scope: string; scope_id: string; capability: string }[];
  user_id?: string; club_id?: string | null; club_collection_release_ids?: string[]; favorite_release_ids?: string[]; can_assign_club?: boolean; can_assign_platform?: boolean };
export type LibraryReview = { id: string; exercise_id: string; source_revision: number; status: string; reason: string | null; can_cancel: boolean; content: LibraryContent };
export type LibrarySelection = { version: 1; release: LibraryRelease } | { version: 1; unavailable: true };
export class ExerciseLibraryError extends Error {
  constructor(readonly code: string, readonly status: number) { super(code); }
}
const base = () => `${getEnv('VITE_AI_API_URL', '').replace(/\/$/, '')}/api/v1/exercise-library`;
export const libraryRequest = async <T>(path = '', options: RequestInit = {}, anonymous = false): Promise<T> => {
  const response = await (anonymous ? fetch : authenticatedFetch)(base() + path, { ...options,
    credentials: anonymous ? 'omit' : 'include', cache: 'no-store',
    headers: anonymous ? undefined : getAuthHeaders({ 'Content-Type': 'application/json', ...Object.fromEntries(new Headers(options.headers).entries()) }) });
  const payload = await response.json();
  if (!response.ok || payload?.ok !== true) throw new ExerciseLibraryError(payload?.detail?.code || 'EXERCISE_STORAGE_UNAVAILABLE', response.status);
  return payload.data as T;
};
export const getLibraryStatus = () => libraryRequest<{ enabled: boolean; available: boolean }>('/status', {}, true);
export const getLibraryCapabilities = () => libraryRequest<LibraryCapabilities>('/capabilities');
export const listLibrary = async <T = LibraryRelease>(query = ''): Promise<T[]> => {
  const result: T[] = [];
  for (let offset = 0; ; offset += 100) {
    const page = await libraryRequest<T[]>(`?limit=100&offset=${offset}&${query}`);
    result.push(...page);
    if (page.length < 100) return result;
  }
};
export const getLibraryDraft = (id: string) => libraryRequest<LibraryDraft>(`/drafts/${encodeURIComponent(id)}`);
export const saveLibraryDraft = async (row: LibraryDraft, content: LibraryContent) => {
  const saved = await libraryRequest<LibraryDraft>(`/drafts/${encodeURIComponent(row.id)}`, { method: 'PUT', body: JSON.stringify({ expected_revision: row.source_revision, content }) });
  if (saved.id !== row.id || saved.source_revision !== row.source_revision + 1) throw new ExerciseLibraryError('EXERCISE_UNVERIFIED_SAVE', 0);
  return saved;
};
export const createLibraryDraft = (content: LibraryContent, owner_type = 'user', club_id: string | null = null) => libraryRequest<LibraryDraft>('', { method: 'POST', body: JSON.stringify({ content, owner_type, club_id }) });
export const libraryTransition = <T = unknown>(path: string, body?: unknown, method = 'POST') => libraryRequest<T>(path, { method, body: body === undefined ? undefined : JSON.stringify(body) });
export const parseLibrarySelection = (raw: unknown): LibrarySelection | undefined => {
  if (raw === undefined) return undefined;
  if (!raw || typeof raw !== 'object' || !('version' in raw) || raw.version !== 1) throw new Error('Unsupported exercise selection');
  if ('unavailable' in raw && raw.unavailable === true) return { version: 1, unavailable: true };
  if (!('release' in raw) || !raw.release || typeof raw.release !== 'object' || !('content' in raw.release) || !('id' in raw.release)) throw new Error('Incomplete exercise selection');
  return structuredClone(raw) as LibrarySelection;
};

export const loadTrainingLibraryReleases = async (): Promise<LibraryRelease[]> => {
  const [publicRows, clubRows, ownRows] = await Promise.all([
    listLibrary('view=discover'), listLibrary('view=club'), listLibrary<LibraryDraft>('view=mine'),
  ]);
  const rows = [...publicRows, ...clubRows, ...ownRows.flatMap(row => row.releases.filter(release => release.status === 'active'))];
  return [...new Map(rows.filter(row => row.reuse.training).map(row => {
    const { status: _status, site_selected: _siteSelected, ...release } = row;
    void _status; void _siteSelected;
    return [row.id, release];
  })).values()];
};
